import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useParams, withRouter } from 'react-router';
import { Helmet } from 'react-helmet-async';
import { axiosKK } from '../services/networkRequest';
import { BoxData, Product, Dimensions } from '../models/APIModels';
import { Formik } from 'formik';
import SubmitButton from '../components/SubmitButton';
import Title from '../components/Title';
import Box from '../components/Box';
import Prices from '../components/Prices';
import Loading from '../components/Loading';
import NavigationStatus from '../components/NavigationStatus';
import { Route } from '../utilities/Route';
import { ShippingPriceDetails } from '../components/ShippingSpecificComponents';
import {
  SuitcaseMobileViewTable,
  SuitcaseTable,
} from '../components/SuitcaseTable';
import {
  DimensionWarningModal,
  WeightWarningModal,
} from '../components/SuitcaseWarningModal';
import { useViewport, Width } from '../utilities/Viewport';
import { SuitcaseCalculatorForm } from '../components/SuitcaseCalculator';
import useGtmEventTracking from '../components/useGtmEventTracking';
import { useHistory } from 'react-router-dom';
import { PreselectedBoxesModal } from '../components/PreselectedBoxesModal';
import { HowItWorksModal } from '../components/HowItWorksModal';
import { displayLoginName } from '../components/user/DisplayLoginName';
import UserContext from '../contexts/UserContext';
import { toMoney } from '../utilities/MoneyConversion';
import { useFlow, FlowType } from '../utilities/useFlow';

interface BoxForm {
  boxes: BoxData[];
}

interface BoxesApiResponse {
  message: string;
  products: BoxData[];
}

// interface BoxesProps extends RouteComponentProps<undefined, StaticContext, BoxesState> {}

// interface BoxesState {
//   storageRequired: boolean;
//   boxes: BoxData[];
//   suitcases?: Dimensions[];
//   suitcaseData?: Product,
// }

interface BoxFormErrors {
  boxes?: string;
}

interface BoxesParams {
  route: Route;
}
const getImageUrlForFlow = (box: BoxData, flow: FlowType): string => {
  const flowPrefix = '/img/keepr/';

  switch (box.name.toLowerCase()) {
    case 'small box ':
    case 'small box':
      return `${flowPrefix}small.png`;
    case 'medium box':
      return `${flowPrefix}medium.png`;
    case 'large box':
      return `${flowPrefix}large.png`;
    case 'extra large box':
      return `${flowPrefix}extra-large.png`;
    case 'suitcase box':
      return `${flowPrefix}suitcase-box.png`;
    case 'suitcase':
      return `${flowPrefix}suitcase.png`;
    case 'own box':
      return `${flowPrefix}own-box.png`;
    case 'packaging tape':
      return `${flowPrefix}packaging-tape.png`;
    case 'bubble wrap':
      return `${flowPrefix}bubble-wrap.png`;
    default:
      return box.imageUrl;
  }
};

function BoxesComponent(): JSX.Element {
  const [boxes, setBoxes] = useState<BoxData[]>([]);
  const { route } = useParams<BoxesParams>();

  const [suitcaseData, setSuitCaseData] = useState<Product>();

  const [suitcases, setSuitcases] = useState<Dimensions[]>([]);
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);

  const isSuitcaseOrder = route === Route.suitcase;

  const { user } = useContext(UserContext);

  const flow = useFlow();

  useEffect(() => {
    let cancelled = false;
    window.scrollTo(0, 0);

    if (isSuitcaseOrder) {
      axiosKK
        .get<{ product: Product }>('/boxes/4')
        .then((response) => {
          if (!cancelled) setSuitCaseData(response.data.product);
        })
        .catch(console.error);
    } else {
      axiosKK
        .get<BoxesApiResponse>('/products/price')
        .then((response) => {
          if (!cancelled) {
            setBoxes(
              response.data.products.map((each) => {
                each.count = 0;
                each.imageUrl = getImageUrlForFlow(each, flow);
                return each;
              }),
            );
          }
        })
        .catch(console.error);
    }

    return () => { cancelled = true; };
  }, [isSuitcaseOrder, flow]);

  const isStorage = route.includes('storage');
  const pageTitle = isStorage
    ? `Box Storage - Choose Your Boxes | ${flow}`
    : `Box Shipping - Choose Your Boxes | ${flow}`;
  const pageDescription = isStorage
    ? `Select from our range of storage boxes. All-inclusive service with boxes, packing materials, collection, storage and delivery from ${flow}.`
    : `Select from our range of shipping boxes. Door-to-door box shipping service across the UK with packing materials included from ${flow}.`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
      </Helmet>
      <section className="login-status-bar">
        <div className="status-bar-content">
          <div className="grey"> {displayLoginName(user)} </div>
        </div>
      </section>
      <div className="navigation-with-button">
        <NavigationStatus step={1} />
      </div>

      {isSuitcaseOrder ? (
        <SuitcaseSelection
          suitcases={suitcases}
          suitcaseData={suitcaseData}
          updateSuitcases={(suitcase) => {
            setSuitcases((prevState) => [...prevState, suitcase]);
          }}
          removeItem={(idx) => {
            setSuitcases((prevState) => {
              const newState = prevState.slice();
              newState.splice(idx, 1);
              return newState;
            });
          }}
          removeAllItem={() => setSuitcases([])}
        />
      ) : (
        <StorageOrShippingSelection
          boxes={boxes}
          setBoxes={setBoxes}
          route={route}
          onShowHowItWorks={() => setShowHowItWorksModal(true)}
        />
      )}

      {/* How It Works Modal */}
      <HowItWorksModal
        show={showHowItWorksModal}
        close={() => setShowHowItWorksModal(false)}
        storageRequired={route.includes(Route.storage)}
      />
    </>
  );
}

interface SuitcaseSelectionProps {
  suitcases: Dimensions[];
  suitcaseData?: Product;
  updateSuitcases: (suitcase: Dimensions) => void;
  removeItem: (idx: number) => void;
  removeAllItem: () => void;
}

interface SuitcaseWarningModalProps {
  dimension: boolean;
  weight?: Dimensions;
}

function SuitcaseSelection({
  updateSuitcases,
  suitcases,
  removeAllItem,
  suitcaseData,
  ...props
}: SuitcaseSelectionProps) {
  const { width } = useViewport();

  const gtmEventTracking = useGtmEventTracking();
  useEffect(() => {
    gtmEventTracking({ event: 'booking_step_suitcase' });
  }, [gtmEventTracking]);

  const history = useHistory();

  const [suitcaseWarningModal, setSuitcaseWarningModal] =
    useState<SuitcaseWarningModalProps>({
      dimension: false,
      weight: undefined,
    });

  return (
    <>
      <Title>Custom Size, tell us more...</Title>
      <p className="grey">
        We just need a few measurements to get you a price for your custom
        items.
      </p>
      <div className="suitcase-selection-component">
        <section className="items-to-send-calculator">
          <div className="title-section">
            <h2>Items to Send</h2>
          </div>
          <div className="calculator">
            <SuitcaseCalculatorForm
              updateSuitcases={updateSuitcases}
              openSuitcaseWeightWarningModal={(suitcase) =>
                setSuitcaseWarningModal((prevState) => ({
                  ...prevState,
                  weight: suitcase,
                }))
              }
              openSuitcaseWarningModal={() =>
                setSuitcaseWarningModal((prevState) => ({
                  ...prevState,
                  dimension: true,
                }))
              }
              removeAllItem={removeAllItem}
            />
          </div>
        </section>
        <section className="suitcase-table-container">
          {width < Width.Tablet ? (
            <SuitcaseMobileViewTable
              suitcases={suitcases}
              suitcaseShippingCost={suitcaseData?.shippingCost}
              {...props}
            />
          ) : (
            <SuitcaseTable
              suitcases={suitcases}
              suitcaseShippingCost={suitcaseData?.shippingCost}
              {...props}
            />
          )}
        </section>
      </div>

      <div className="suitcase-price-and-next">
        <Prices
          boxes={[]}
          storageRequired={false}
          suitcases={suitcases}
          suitcaseShippingCost={suitcaseData?.shippingCost}
        />

        <SubmitButton
          disabled={suitcases.length <= 0}
          onClick={() => {
            history.push(`/suitcase/collection_address`, {
              boxes: [],
              storageRequired: false,
              suitcases,
              suitcaseData,
            });
          }}
        >
          Next
          <img alt="" src="/img/right-arrow-circular-button-outline.png" />
        </SubmitButton>
      </div>

      {suitcaseWarningModal.dimension && (
        <DimensionWarningModal
          close={() =>
            setSuitcaseWarningModal((prevState) => ({
              ...prevState,
              dimension: false,
            }))
          }
        />
      )}
      {suitcaseWarningModal.weight && (
        <WeightWarningModal
          close={() =>
            setSuitcaseWarningModal((prevState) => ({
              ...prevState,
              weight: undefined,
            }))
          }
          mainButtonAction={() => {
            updateSuitcases(suitcaseWarningModal.weight!);
            setSuitcaseWarningModal((prevState) => ({
              ...prevState,
              weight: undefined,
            }));
          }}
        />
      )}
    </>
  );
}

interface StorageOrShippingSelectionProps {
  route: Route;
  boxes: BoxData[];
  setBoxes: Dispatch<SetStateAction<BoxData[]>>;
  onShowHowItWorks: () => void;
}

function StorageOrShippingSelection({
  route,
  boxes,
  setBoxes,
  onShowHowItWorks,
}: StorageOrShippingSelectionProps) {
  const history = useHistory();
  const initialValues: BoxForm = {
    boxes: [],
  };

  const storageRequired = route.includes(Route.storage);
  const effect = storageRequired ? 'booking_step_storage' : 'booking_step_ship';

  const gtmEventTracking = useGtmEventTracking();
  useEffect(() => {
    gtmEventTracking({ event: effect });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gtmEventTracking]);

  const deliveryFeeString = process.env.REACT_APP_DELIVERY_FEE;

  if (!deliveryFeeString) {
    throw new Error('No delivery fee set');
  }

  const deliveryFee = parseInt(deliveryFeeString);

  return (
    <>
      <div className="boxes-page-header">
        <Title>{`What do you want to ${
          storageRequired ? 'store' : 'ship'
        }?`}</Title>
        {!storageRequired && (
          <p className="grey">
            Kit Keeper are experts at storing and shipping boxes all across the
            UK.
          </p>
        )}
        <div className="boxes-page-controls">
          {storageRequired && (
            <button
              className="how-it-works-button"
              type="button"
              onClick={() => history.push(`/storage/boxes/packages`)}
            >
              Suggested Packages
            </button>
          )}
          <button
            className="how-it-works-button"
            onClick={onShowHowItWorks}
            type="button"
          >
            How it works
          </button>
        </div>
      </div>
      <Formik
        initialValues={initialValues}
        validateOnMount={true}
        validate={(values) => {
          const errors: BoxFormErrors = {};

          if (
            !values.boxes ||
            values.boxes.length === 0 ||
            values.boxes.reduce((acc, each) => each.count + acc, 0) === 0
          ) {
            errors.boxes = 'No boxes selected';
          }

          return errors;
        }}
        onSubmit={() =>
          history.push(`/${route}/collection_address`, {
            boxes: boxes.filter((value) => value.count),
            storageRequired,
          })
        }
        validateOnChange={true}
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          isValid,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <section className="boxes-and-cost-section">
              <div className="all-items">
                {boxes && boxes.length ? (
                  boxes
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((box, index) => (
                      <Box
                        key={box.id}
                        box={{
                          ...box,
                          count:
                            values.boxes.find((b) => b.id === box.id)?.count ||
                            box.count,
                        }}
                        setBox={boxChange(
                          index,
                          boxes,
                          (boxes) => {
                            setBoxes(boxes);
                          },
                          setFieldValue,
                        )}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        storageRequired={storageRequired}
                      />
                    ))
                ) : (
                  <Loading />
                )}
              </div>
              {storageRequired ? null : <ShippingPriceDetails boxes={boxes} />}
            </section>
            {storageRequired ? (
              <Prices boxes={values.boxes} storageRequired={storageRequired} />
            ) : null}
            <SubmitButton disabled={!isValid} isSubmitting={isSubmitting}>
              Next
              <img alt="" src="/img/right-arrow-circular-button-outline.png" />
            </SubmitButton>
            <p className="grey">
              25kg maximum weight per box.
              <br />
              <br />
              Delivery of ordered boxes and materials
              <br />
              (delivers in 1-3 working days from placing order): {toMoney(deliveryFee)}
            </p>
            {history.location.pathname === '/storage/boxes/packages' && (
              <PreselectedBoxesModal
                close={() => {
                  history.push('/storage/boxes');
                }}
                onSelectPredefinedBoxes={(preselectedBoxes) => {
                  boxes.forEach((box) => {
                    box.count = 0;
                  });

                  for (const [key, value] of Object.entries(preselectedBoxes)) {
                    const box = boxes.find((box) => box.id === Number(key));

                    if (box) {
                      box.count = value;
                    }
                  }

                  setFieldValue('boxes', boxes, true);
                  history.push('/storage/boxes');
                }}
              />
            )}
          </form>
        )}
      </Formik>
    </>
  );
}

function boxChange(
  i: number,
  boxes: BoxData[],
  setBoxes: (boxes: BoxData[]) => void,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
): (box: BoxData) => BoxData {
  return (box: BoxData) => {
    boxes[i] = box;
    setBoxes(boxes);

    setFieldValue('boxes', boxes, true);

    return box;
  };
}

export default withRouter(BoxesComponent);
